import { Injectable } from "@angular/core";
import { ApiMonitorService, MetricCollector, RepositoryService } from "@tylertech/core-module";
import { getLoggerFor, LogInvocation, LogLevel } from "@tylertech/logging";
import { ODataFilterBuilder, ODataFilterFactoryService } from "@tylertech/odata-module";
import { Utilities } from "@tylertech/shared-module";
import { AfterSaveContext, Align, IRepositoryRecalc, Key, ModifierKey, PropertyMetadata, RecalculateProperty, RegexFormattingOptions, RepositoryDataCollection, RepositoryDataCollectionRecalc, Tuple, } from "@tylertech/tyler-api-types";
import { AugmentedModelObject, MetadataKey, RepositoryFormData } from "../interfaces";
import { ArrayHelpers, MetadataKeyHelpers } from "../utilities";
import { MetadataService } from "./metadata.service";
import { MetadataActionService } from "./metadataActionService";
import { BreadcrumbArg, MetadataEventService } from "./metadataEventService";
import { MetadataInputTypeHelperService } from "./metadataInputTypeHelperService";

// REVIEW - would it make sense to make this service only have one method that returns an instance helper? it might cut down on the repeated parameters like propertyMetadata, parent, and model.
@Injectable({ providedIn: "root" })
export class MetadataPropertyService
{
    private readonly log = getLoggerFor("MetadataPropertyService");
    private recalculateAll = false;
    recalculateVQHActionInterrupt: boolean = false; //Recalc was interrupted by a VQH action dialog and is done recalculating, but cleanup will not happen until action dialog closes
    returnFocusToAfterRecalculate: any; //after a recalculate, a pre-defined element to retake focus

    constructor(
        apiMonitor: ApiMonitorService,
        private readonly $metadataService: MetadataService,
        private $metadataInputTypeHelperService: MetadataInputTypeHelperService,
        private readonly $odataFilterFactory: ODataFilterFactoryService,
        private readonly $metadataActionService: MetadataActionService,
        private $metadataEventService: MetadataEventService,
        private readonly $repositoryService: RepositoryService
    )
    {
        apiMonitor.monitorLongApiCalls(
            "IRepositoryService/RecalculateProperties",
            ((model: IRepositoryRecalc) =>
            {
                return {
                    Type: model.RepositoryType,
                    Property: model.ChangingProperty,
                };
            }) as MetricCollector);
    }

    isRequired(property: PropertyMetadata, model: any, parent?: any): boolean
    {
        /*
         * We can't trust truthy/falsy evaluation here because we are dealing with boolean primitives.
         * if the propertyMetadata's has a value, it MUST supersede the dynamically evaluated field.
         */
        return Utilities.IsNullOrUndefined(property.Required)
            ? property.RequiredProperty &&
            this.$metadataService.EvaluateBoolean(property.RequiredProperty, parent, model)
            : property.Required;
    }

    isHidden(property: PropertyMetadata, model: any, parent?: any): boolean
    {
        /*
         * We can't trust truthy/falsy evaluation here because we are dealing with boolean primitives.
         * if the propertyMetadata's has a value, it MUST supersede the dynamically evaluated field.
         */
        return Utilities.IsNullOrUndefined(property.Hidden)
            ? property.HiddenProperty && this.$metadataService.EvaluateBoolean(property.HiddenProperty, parent, model)
            : property.Hidden;
    }

    getMaintainWidthWhenHidden(property: PropertyMetadata): boolean
    {
        return Utilities.IsNullOrUndefined(property.MaintainWidthWhenHidden) ? false : property.MaintainWidthWhenHidden;
    }

    isDisabled(property: PropertyMetadata, model: any, parent?: any): boolean
    {
        /*
         * We can't trust truthy/falsy evaluation here because we are dealing with boolean primitives.
         * if the propertyMetadata's has a value, it MUST supersede the dynamically evaluated field.
         */
        if (!this.hasAllDependentValues(property, model, parent)) return true;
        return Utilities.IsNullOrUndefined(property.Disabled)
            ? property.DisabledProperty &&
            this.$metadataService.EvaluateBoolean(property.DisabledProperty, parent, model)
            : property.Disabled;
    }

    isAllowAdd(property: PropertyMetadata, model: any, parent?: any): boolean
    {
        return Utilities.IsNullOrUndefined(property.AllowAdd)
            ? property.AllowAddProperty &&
            this.$metadataService.EvaluateBoolean(property.AllowAddProperty, parent, model)
            : property.AllowAdd;
    }

    isAllowEdit(property: PropertyMetadata, model: any, parent?: any): boolean
    {
        return Utilities.IsNullOrUndefined(property.AllowEdit)
            ? property.AllowEditProperty &&
            this.$metadataService.EvaluateBoolean(property.AllowEditProperty, parent, model)
            : property.AllowEdit;
    }

    isViewOnly(property: PropertyMetadata, model: MetadataModel, parent?: any): boolean
    {
        return (
            this.isDisabled(property, model, parent) ||
            (!property.ViewOnlyEnabled && this.AreAnyControllingPropertiesMissingValues(property, model, parent))
        );
    }

    isSingleLine(property: PropertyMetadata): boolean
    {
        return Utilities.IsNullOrUndefined(property.SingleLine) ? false : property.SingleLine;
    }

    isBeingRecalculated(property: PropertyMetadata, model: MetadataModel): boolean
    {
        return !this.recalculateVQHActionInterrupt && (this.recalculateAll || (model.$recalculating && ArrayHelpers.any(model.$recalculatingProperties, x => x === property.Name)));
    }

    getLabel(property: PropertyMetadata, model: any, parent?: any): string
    {
        return this.$metadataService.EvaluateString(property.Label, parent, model);
    }

    getAlign(property: PropertyMetadata): Align
    {
        return property && property.Alignment ? property.Alignment : Align.Left;
    }

    getRegex(property: PropertyMetadata, metadataModel: any, parent?: any): string
    {
        return property.Regex
            ? property.Regex
            : !property.RegexProperty
                ? null
                : this.$metadataService.DependentModuleValue(property.RegexProperty, parent, metadataModel);
    }

    hasIcon(property: PropertyMetadata): boolean
    {
        return !Utilities.IsNullOrUndefined(property.Icon) || !Utilities.IsNullOrUndefined(property.IconProperty);
    }

    getIcon(property: PropertyMetadata, model: any, parent?: any): string
    {
        return Utilities.IsNullOrUndefined(property.Icon)
            ? property.IconProperty && this.$metadataService.DependentModuleValue(property.IconProperty, parent, model)
            : property.Icon;
    }

    getRegexFormatter(property: PropertyMetadata, metadataModel: any, parent?: any): RegexFormattingOptions
    {
        return property.RegexOptions
            ? property.RegexOptions
            : !property.RegexOptionsProperty
                ? null
                : this.$metadataService.DependentModuleValue(property.RegexOptionsProperty, parent, metadataModel);
    }

    hasAllDependentValues(propertyMetadata: PropertyMetadata, model: any, parent?: any): boolean
    {
        if (!propertyMetadata.DependentUpon)
        {
            return true;
        }
        const dependentUponProperties = propertyMetadata.DependentUpon.split(",");
        const self = this;
        const dependentCount = dependentUponProperties.length;
        return (
            dependentUponProperties.reduce(
                (c, n) =>
                    c +
                    (Utilities.IsNullOrUndefinedOrZero(self.$metadataService.DependentModuleValue(n, parent, model))
                        ? 0
                        : 1),
                0
            ) === dependentCount
        );
    }

    getDependentValuesForProperty(propertyMetadata: PropertyMetadata, model: any, parent?: any): any[]
    {
        if (!propertyMetadata.DependentUpon)
        {
            return null;
        }
        const dependentUponProperties = propertyMetadata.DependentUpon.split(",");
        const self = this;
        return dependentUponProperties.map(x => self.$metadataService.DependentModuleValue(x, parent, model));
    }

    getFilterByValuesForProperty(propertyMetadata: PropertyMetadata, model: any, parent?: any): any[]
    {
        if (!propertyMetadata.FilterBy)
        {
            return null;
        }
        const filterByProperties = this.split(propertyMetadata.FilterBy);
        const self = this;
        return filterByProperties.map(x => self.$metadataService.DependentModuleValue(x, parent, model));
    }

    private split(value: string): string[]
    {
        return value.split(",");
    }

    AreAnyControllingPropertiesMissingValues(propertyMetadata: PropertyMetadata, model: any, parent?: any): boolean
    {
        const list = this.getDependentValuesForProperty(propertyMetadata, model, parent);
        return ArrayHelpers.any(list, f => !f || MetadataKeyHelpers.isNullKey(<MetadataKey>f));
    }

    createOdataFilter(
        propertyMetadata: PropertyMetadata,
        metadataModel: any,
        parent?: any,
        searchText?: string,
        key?: string,
        top: number = 0,
        skip: number = 0
    ): string
    {
        const self = this;
        const odataFilterBuilder = this.$odataFilterFactory.createBuilder();
        if (propertyMetadata.DependentUpon)
        {
            if (this.AreAnyControllingPropertiesMissingValues(propertyMetadata, metadataModel, parent))
            {
                return null;
            }
            appendFilter(odataFilterBuilder, this.split(propertyMetadata.DependentUpon));
        }
        if (propertyMetadata.FilterBy)
        {
            appendFilter(odataFilterBuilder, this.split(propertyMetadata.FilterBy));
        }
        if (searchText)
        {
            if (searchText.endsWith("*") && searchText.length > 1)
                odataFilterBuilder.startsWith("Value", Utilities.toLower(searchText.substring(0, searchText.length - 1)));
            else if (searchText.startsWith("*") && searchText.length > 1)
                odataFilterBuilder.endsWith("Value", Utilities.toLower(searchText.substring(1, searchText.length)));
            else
                odataFilterBuilder.like("Value", Utilities.toLower(searchText));
        }
        if (key)
        {
            odataFilterBuilder.filterEQ("Key", key);
        }
        if (top > 0)
        {
            odataFilterBuilder.top(top);
        }
        if (skip > 0)
        {
            odataFilterBuilder.skip(skip);
        }
        if (propertyMetadata.ConstantFilter)
        {
            let constantFilter = this.$metadataService.EvaluateString(propertyMetadata.ConstantFilter, parent, metadataModel, null, true);
            if (constantFilter)
                odataFilterBuilder.constantFilter(`${constantFilter}`);
        }
        return odataFilterBuilder.build();
        function appendFilter(builder: ODataFilterBuilder, list: string[]): void
        {
            for (const depString of list.filter(x => !!x))
            {
                let field = depString.split("|")
                    .pop();
                field = field.split(".")
                    .pop();
                const value = self.$metadataService.DependentModuleValue(depString, parent, metadataModel);
                if (!Utilities.IsNullOrUndefined(value))
                {
                    if (value.constructor !== Array)
                        builder.filterEQ(field, value);
                    else if (value.length > 0)
                        builder.in(field, value);
                }
            }
        }
    }

    setBustCache(propertyMetadata: PropertyMetadata): void
    {
        if (
            this.$metadataInputTypeHelperService.isSelect(propertyMetadata.InputType) ||
            this.$metadataInputTypeHelperService.isMultiSelect(propertyMetadata.InputType) ||
            this.$metadataInputTypeHelperService.isDualList(propertyMetadata.InputType) ||
            this.$metadataInputTypeHelperService.isTypeAhead(propertyMetadata.InputType)
        )
        {
            (propertyMetadata as CacheablePropertyMetadata).$$forceReload = true;
        }
    }

    private AreEqual(one: any, two: any): boolean
    {
        if (!Utilities.IsNullOrUndefined(one) && one["$isEntityWrapper"]) one = one["$value"];
        if (two["$isEntityWrapper"]) two = two["$value"];
        return one === two;
    }

    FindInParent(parent: any, toFind: any): Tuple<string, number>
    {
        for (const field in parent)
        {
            const a = parent[field];
            if (Array.isArray(a))
            {
                for (let i = 0; i < a.length; i++)
                {
                    const c = a[i];
                    if (this.AreEqual(c, toFind))
                    {
                        return <Tuple<string, number>>{
                            Item1: field,
                            Item2: i,
                        };
                    }
                }
            }
            else if (this.AreEqual(a, toFind))
            {
                return <Tuple<string, number>>{ Item1: field };
            }
        }
    }

    /**
     * Makes a call to the IRepositoryService's RecalculateProperties endpoint,
     * with the given context, and applies the results to the `metadataModel`.
     */
    @LogInvocation(LogLevel.DEBUG, true)
    recalculateProperties(
        typeName: string,
        propertyMetadata: PropertyMetadata,
        metadataModel: MetadataModel,
        parent: any,
        newValue: any,
        confirmationQuestions: Record<string, boolean>,
        reInitView: boolean,
        showLoading: boolean,
        formData?: RepositoryFormData
    ): Promise<AfterSaveContext>
    {
        const self = this;
        this.recalculateAll = propertyMetadata.RecalculateOnChange
            ? propertyMetadata.RecalculateOnChange[0] === "$All"
            : false;
        metadataModel.$recalculating = true;
        metadataModel.$recalculatingProperties = propertyMetadata.RecalculateOnChange.map(x => x);
        const stringifiedNewValue =
            Utilities.IsNullOrUndefinedOrEmpty(newValue) && !Array.isArray(newValue) && newValue !== ""
                ? null
                : Utilities.stringify(newValue);
        let confirmationModel = formData
            ? formData
            : <RepositoryFormData>{ ConfirmationQuestions: confirmationQuestions };
        let wholeFormRecalc = null;
        let expectedFormData = null;
        function crawlSupPropertyChain(current: any, propertyList: Tuple<string, number>[]): any
        {
            if (current["$FormData"] && (!expectedFormData || current["$FormData"] === expectedFormData))
            {
                confirmationModel = current["$FormData"];
                return current;
            }
            else
            {
                const currentparent = current["$$Parent"];
                const inParent = self.FindInParent(currentparent, current);
                propertyList.unshift(inParent);
                return crawlSupPropertyChain(currentparent, propertyList);
            }
        }
        if (propertyMetadata.RecalculateOnChange[0] === "$WholeFormData")
        {
            this.recalculateAll = true;
            let subPropertyList: Tuple<string, number>[] = [];
            let formDataObject = crawlSupPropertyChain(metadataModel, subPropertyList);
            let typeToUse = formDataObject["$$TypeName"];
            let indexToUse = null;
            confirmationModel = formDataObject["$FormData"];
            let parentModel = confirmationModel.$ParentFormData;
            if (
                parentModel &&
                propertyMetadata.RecalculateOnChange.length > 1 &&
                propertyMetadata.RecalculateOnChange[1] === "$ParentWholeFormData"
            )
            {
                const originalFormData = confirmationModel;
                expectedFormData = parentModel;
                parentModel.InitializeFormData();
                subPropertyList = [];
                formDataObject = crawlSupPropertyChain(metadataModel, subPropertyList);
                typeToUse = formDataObject["$$TypeName"];
                confirmationModel = formDataObject["$FormData"];
                parentModel = null;
                originalFormData.InitializeFormData();
            }
            if (confirmationModel.Type !== typeToUse)
            {
                const others = confirmationModel.OtherEntities[typeToUse];
                for (let index = 0; index < others.length; index++)
                {
                    const element = others[index];
                    if (self.AreEqual(element, metadataModel))
                    {
                        indexToUse = index;
                        break;
                    }
                }
            }
            wholeFormRecalc = <RepositoryDataCollectionRecalc>{
                StringifiedNewValue: stringifiedNewValue,
                ChangingProperty: propertyMetadata.Name,
                FormData: <RepositoryDataCollection>Utilities.deepCopy(confirmationModel),
                TypeToUse: typeToUse,
                TypeIndexToUse: indexToUse,
                SubPropertyList: subPropertyList,
            };
        }
        const callback = (): Promise<AfterSaveContext> =>
            self.recalculateProperties(
                typeName,
                propertyMetadata,
                metadataModel,
                parent,
                newValue,
                confirmationModel.ConfirmationQuestions,
                reInitView,
                showLoading,
                formData
            );
        const rejectionHandler = this.$metadataActionService.GetVerificationHandler(
            confirmationModel,
            callback,
            metadataModel
        );
        this.$metadataEventService.fireRecalculateWorkingListeners(
            {
                ShowLoading: showLoading,
                Recalculating: true,
            },
            reInitView
        );
        if (wholeFormRecalc)
        {
            return this.$repositoryService
                .RecalculateDataCollection(wholeFormRecalc)
                .then(
                    x =>
                    {
                        // merge changes from recalculate before the next action
                        confirmationModel.Merge(x);
                        self.$metadataActionService.ProcessAfterSave(x.AfterSaveContext, metadataModel, formData);
                        if (self.isViewMetadataRefresh(propertyMetadata.RecalculateOnChange))
                            self.$metadataEventService.fireMetadataRefreshListeners();
                        else
                            self.$metadataService.FireGridReload(metadataModel.$$TypeName, true);
                        self.$metadataEventService.fireRecalculateWorkingListeners(
                            {
                                ShowLoading: showLoading,
                                Recalculating: false,
                            },
                            true
                        );
                        return x.AfterSaveContext;
                    },
                    e => handleError(e)
                )
                .finally(cleanUp);
        }

        let stringifiedEntity: string;
        if (propertyMetadata.RecalculateDoNotSend)
            stringifiedEntity = stringifyWithIgnore(metadataModel, propertyMetadata.RecalculateDoNotSend);
        else
            stringifiedEntity = Utilities.stringify(metadataModel);

        const param = <IRepositoryRecalc>{
            RepositoryType: typeName,
            StringifiedEntity: stringifiedEntity,
            StringifiedNewValue: stringifiedNewValue,
            PropertiesToRecalc: propertyMetadata.RecalculateOnChange,
            ChangingProperty: propertyMetadata.Name,
            ConfirmationQuestions: confirmationQuestions,
        };
        if (propertyMetadata.RecalculateToIgnore)
            param.PropertiesToIgnore = propertyMetadata.RecalculateToIgnore;
        if (propertyMetadata.RecalculateWithParent && parent?.$$TypeName)
        {
            let inParent = null;
            if (propertyMetadata.RecalculateDoNotSend?.some(x => x.startsWith("Parent.")))
            {
                param.OptionalStringifiedParent = stringifyWithIgnore(parent, propertyMetadata.RecalculateDoNotSend.filter(x =>
                    x.startsWith("Parent.")).map(x => x.substring(7)));
            }
            else
            {
                param.OptionalStringifiedParent = Utilities.stringify(parent);
                inParent = this.FindInParent(parent, metadataModel);
            }
            param.OptionalParentRepositoryType = parent.$$TypeName;
            if (inParent)
            {
                param.StringifiedEntity = undefined;
                param.PropertyFromParent = inParent.Item1;
                param.PropertyFromParentIndex = inParent.Item2;
                if (!Utilities.IsNullOrUndefined(metadataModel["Id"]))
                    param.PropertyFromParentId = metadataModel["Id"];
            }
        }
        return this.$repositoryService
            .RecalculateProperties(param)
            .then(
                x =>
                {
                        // If cache for the grid type needs to be cleared, refetch the grid after processing AfterSaveContext (clearing the cache)
                        const delayReloadEvents = !Utilities.IsNullOrUndefined(x?.AfterSaveContext?.LookupsToRefresh);
                        processResult(x.Entity, delayReloadEvents);
                        let afterSave = self.$metadataActionService.ProcessAfterSave(x.AfterSaveContext, metadataModel, formData);
                        if (x.AfterSaveContext)
                            x.AfterSaveContext["$NextActionChain"] = afterSave;

                        if (delayReloadEvents)
                        {
                            fireReloadEvents(true);
                        }
                        return x.AfterSaveContext;
                },
                e => handleError(e)
            )
            .finally(cleanUp);
        function processResult(recalculatedProperties: any, delayReloadEvents: boolean): void
        {
            if (propertyMetadata.RecalculateOnChange[0] === "$All")
            {
                if (propertyMetadata.RecalculateOnChange.length > 1)
                {
                    for (let index = 1; index < propertyMetadata.RecalculateOnChange.length; index++)
                    {
                        const property = propertyMetadata.RecalculateOnChange[index];
                        if (property !== "$ViewMetadata")
                        {
                            self.$metadataService.SetModuleValue(
                                property,
                                parent,
                                metadataModel,
                                recalculatedProperties[property]
                            );
                            delete recalculatedProperties[property];
                        }

                    }
                }
                if (propertyMetadata.RecalculateToIgnore?.length > 0)
                    propertyMetadata.RecalculateToIgnore.forEach(property => recalculatedProperties[property] = metadataModel[property]);

                Utilities.merge(metadataModel, recalculatedProperties);
                if (propertyMetadata.ShortcutMetadata?.ModifierKey === ModifierKey.Alt && propertyMetadata.ShortcutMetadata?.Key === Key.C)
                {
                    self.$metadataEventService.fireSearchFormBreadcrumbListener(
                        <BreadcrumbArg>{
                            Field: "$Clear"
                        });
                }
            }
            else
            {
                for (const property of propertyMetadata.RecalculateOnChange)
                {
                    self.$metadataService.SetModuleValue(
                        property,
                        parent,
                        metadataModel,
                        recalculatedProperties[property]
                    );
                    self.$metadataEventService.fireSearchFormBreadcrumbListener(
                        <BreadcrumbArg>{
                            Field: property,
                            Value: recalculatedProperties[property]
                        });
                }
            }
            if (!delayReloadEvents)
            {
                fireReloadEvents(false);
            }
        }
        function fireReloadEvents(refetch: boolean): void
        {
            if (propertyMetadata.RecalculateOnChange[0] === "$Nothing")
            {
                self.$metadataEventService.fireRecalculateWorkingListeners(
                    {
                        ShowLoading: false,
                        Recalculating: false,
                    },
                    true
                );
                return;
            }

            if (self.isViewMetadataRefresh(propertyMetadata.RecalculateOnChange))
                self.$metadataEventService.fireMetadataRefreshListeners();
            else
            {
                if (refetch)
                {
                    self.$metadataService.FireGridRefetch(metadataModel.$$TypeName);
                }
                else
                {
                    const clearGridFilter = Object.values<string>(ClearTypes).includes(propertyMetadata.Name);
                    const applyGridFilter = Object.values<string>(ApplyTypes).includes(propertyMetadata.Name);
                    if (applyGridFilter)
                        metadataModel["$applyProperty"] = propertyMetadata;
                    self.$metadataService.FireGridReload(metadataModel.$$TypeName, true, param.PropertiesToRecalc, false, clearGridFilter, applyGridFilter);
                }
            }
            self.$metadataEventService.fireRecalculateWorkingListeners(
                {
                    ShowLoading: showLoading,
                    Recalculating: false,
                },
                true
            );
        }
        function handleError(e: any): Promise<any>
        {
            if (e.error?.Action && JSON.parse(e.error?.Action).ViewToShow)
                self.recalculateVQHActionInterrupt = true;
            self.$metadataEventService.fireRecalculateWorkingListeners(
                {
                    ShowLoading: showLoading,
                    Recalculating: false,
                },
                reInitView
            );
            return rejectionHandler(e);
        }

        function cleanUp(): void
        {
            delete metadataModel.$recalculating;
            delete metadataModel.$recalculatingProperties;
            if (formData && !Utilities.IsNullOrUndefined(formData["$setInitialModCounter"]))
            {
                if (formData["$setInitialModCounter"] > 1)
                    formData["$setInitialModCounter"]--;
                else
                {
                    formData.SetInitialModCount();
                    delete formData["$setInitialModCounter"];
                }
            }

            self.recalculateAll = false;
            self.$metadataEventService.fireSaveAfterRecalculateListener();
            requestAnimationFrame(() =>
            {
                if (self.returnFocusToAfterRecalculate)
                {
                    self.returnFocusToAfterRecalculate.focus();
                    self.returnFocusToAfterRecalculate = undefined;
                }
                else
                {
                    const el = (window.document.activeElement as HTMLInputElement);
                    if (el.setSelectionRange && el.type !== "checkbox")
                        el?.setSelectionRange(0, el.value.length);
                }
            });
        }
    }

    protected isViewMetadataRefresh(properties: string[]): boolean
    {
        if (properties[0] === "$ViewMetadata")
            return true;
        if (properties.length > 1 && properties[1] === "$ViewMetadata")
            return true;
        return false;
    }

    getPropertiesToRecalculate(recalculateProperty: RecalculateProperty, supportWholeFormRecalculate: boolean = false): string[]
    {
        if (recalculateProperty.RecalculateViewMetadata)
        {
            if (Utilities.IsNullOrUndefined(recalculateProperty.PropertiesToRecalculate))
                recalculateProperty.PropertiesToRecalculate = [];
            recalculateProperty.PropertiesToRecalculate.unshift("$ViewMetadata");
            if (recalculateProperty.PropertiesToRecalculate.length === 1 && supportWholeFormRecalculate && recalculateProperty.RecalculateWholeFormData)
                recalculateProperty.PropertiesToRecalculate.unshift("$WholeFormData");
        }

        if (Utilities.IsNullOrUndefinedOrEmpty(recalculateProperty.PropertiesToRecalculate))
            return supportWholeFormRecalculate && recalculateProperty.RecalculateWholeFormData
                ? ["$WholeFormData"]
                : ["$All"];
        return recalculateProperty.PropertiesToRecalculate;
    }

}

function stringifyWithIgnore(model: unknown, ignore: string[]): string
{
    //Cannot apply ignore to jsonNullRemover as it will remove all nested instances of the property name as well.
    const modelToStringify = {};
    for (const key of Object.keys(model))
    {
        if (ignore.indexOf(key) > -1)
            modelToStringify[key] = undefined;
        else
            modelToStringify[key] = model[key];
    }
    return Utilities.stringify(modelToStringify);
}

export interface CacheablePropertyMetadata extends PropertyMetadata
{
    InputType: "Select" | "MultiSelect" | "TypeAhead" | "DualList";
    $$forceReload?: boolean;
}
export interface MetadataModel extends AugmentedModelObject
{
    /**
     * @private Flag to determine whether this model is currently waiting on a call to
     * `IRepositoryService.RecalculateOnChange` to be fully resolved.
     *
     * There are certain instances where we need to wait till all of the recalculated properties
     * have been updated before evaluating dependent effects.
     */
    $recalculating?: boolean;
    $recalculatingProperties?: string[];
}

enum ClearTypes
{
    Clear = "cmdClear",
    ClearButton = "ClearButton"
}

enum ApplyTypes
{
    ApplySel = "cmdApplySel",
    Apply = "cmdApply",
    SearchButton = "SearchButton"
}

export const enum ElementTheme
{
    Error = "error",
    Warning = "warning",
    Success = "success",
    Default = "primary",
}
